"use client";

import { createAuthClient } from "better-auth/react";

/**
 * The dashboard authenticates against the gateway's better-auth instance (`/api/auth/*`):
 * passwordless email OTP to sign in / register, TOTP 2FA before high-value actions. No
 * password anywhere. `credentials: "include"` sends the httpOnly session cookie the gateway
 * sets (its CORS allow-list must include this origin — GATEWAY_ALLOWED_ORIGINS).
 *
 * The OTP/2FA flows are driven through the client's `$fetch` against the real better-auth
 * endpoints rather than the typed client plugins: better-auth 1.2.7's client-plugin types
 * have an internal inconsistency that degrades the whole client type. `$fetch` hits the exact
 * same endpoints (with the client's cookie/CSRF handling) and keeps everything strictly typed.
 */
const GATEWAY_URL =
  process.env.NEXT_PUBLIC_GATEWAY_URL ?? "http://localhost:8090";

export const authClient = createAuthClient({
  baseURL: `${GATEWAY_URL}/api/auth`,
  fetchOptions: { credentials: "include" },
});

export const { useSession, signOut } = authClient;

type AuthResult<D = unknown> = {
  data: D | null;
  error: { message?: string } | null;
};

/** Send a 6-digit sign-in code. SIGN-IN ONLY — an unknown email creates no account
 * (`disableSignUp` in services/gateway/src/auth.ts); register at /signup instead. */
export const sendOtp = (email: string) =>
  authClient.$fetch("/email-otp/send-verification-otp", {
    method: "POST",
    body: { email, type: "sign-in" },
  }) as Promise<AuthResult>;

/** Verify the code. `data.twoFactorRedirect` is true when the account has TOTP 2FA enabled. */
export const signInOtp = (email: string, otp: string) =>
  authClient.$fetch("/sign-in/email-otp", {
    method: "POST",
    body: { email, otp },
  }) as Promise<AuthResult<{ twoFactorRedirect?: boolean }>>;

// ---- Email + password (primary) + remember-me -----------------------------------------

/** Register with email + password (auto-signs-in). `name` is optional. */
export const signUpEmail = (email: string, password: string, name?: string) =>
  authClient.$fetch("/sign-up/email", {
    method: "POST",
    body: { email, password, name: name ?? email.split("@")[0] },
  }) as Promise<AuthResult>;

/**
 * Sign in with email + password. `rememberMe=false` → session ends on browser close.
 * NOTE: every session is capped at ONE HOUR server-side regardless of this flag
 * (services/gateway/src/auth.ts), so it now only chooses persistent vs browser-session
 * COOKIE within that hour. The login UI no longer offers it as a choice.
 */
export const signInPassword = (
  email: string,
  password: string,
  rememberMe: boolean,
) =>
  authClient.$fetch("/sign-in/email", {
    method: "POST",
    body: { email, password, rememberMe },
  }) as Promise<AuthResult>;

/** Start a password reset: emails a link back to `${origin}/reset?token=…` (Resend). */
export const forgetPassword = (email: string) =>
  authClient.$fetch("/request-password-reset", {
    method: "POST",
    body: { email, redirectTo: `${window.location.origin}/reset` },
  }) as Promise<AuthResult>;

/**
 * Complete a password reset: the token from the emailed LINK plus the 6-digit CODE from the second
 * email.
 *
 * Deliberately NOT `authClient.$fetch("/reset-password")`. That endpoint takes only {token,
 * newPassword}, so routing through it would make the second factor optional. The gateway seals it
 * off with a 403 and exposes this route instead, which verifies the code before touching the
 * password and then calls better-auth server-side — so better-auth still owns hashing, token
 * validation and session revocation.
 */
export const resetPassword = async (
  newPassword: string,
  token: string,
  code: string,
): Promise<{ error?: string }> => {
  try {
    const res = await fetch(`${GATEWAY_URL}/auth/reset/complete`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ token, code, newPassword }),
    });
    if (res.ok) return {};
    const body = (await res.json().catch(() => null)) as {
      error?: string;
    } | null;
    return { error: body?.error ?? `reset failed (${res.status})` };
  } catch {
    return { error: "could not reach the server — check your connection" };
  }
};

/**
 * Change password from the settings page.
 *
 * Requires the current password AND a live step-up window (a fresh emailed code). Routed through
 * the gateway, not `authClient.$fetch("/change-password")`: better-auth's endpoint takes only
 * {currentPassword, newPassword}, so calling it directly would skip the step-up. The gateway
 * returns 403 on that path and enforces elevation here, then calls better-auth server-side — so it
 * still verifies the current password, hashes the new one and revokes other sessions.
 */
export const changePassword = async (
  currentPassword: string,
  newPassword: string,
): Promise<{ error?: string; stepUpRequired?: boolean }> => {
  try {
    const res = await fetch(`${GATEWAY_URL}/me/change-password`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ currentPassword, newPassword }),
    });
    if (res.ok) return {};
    const body = (await res.json().catch(() => null)) as {
      error?: string;
    } | null;
    if (res.status === 403 && body?.error === "step_up_required") {
      return {
        stepUpRequired: true,
        error: "Your step-up window expired — verify a fresh code.",
      };
    }
    return {
      error: body?.error ?? `could not change password (${res.status})`,
    };
  } catch {
    return { error: "could not reach the server — check your connection" };
  }
};
